import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { ButtonLegacy, type ButtonProps } from '@/components/ui/button-legacy'
import { Dropzone } from '@/components/ui/dropzone'
import { PlusIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { VETTING_MAX_FILE_TO_UPLOAD } from '@/features/ops-vetting/costants.ts'
import { httpConfirmUploadPhotoProPhotos } from '@/features/service-vetting/http/services-vetting.http.ts'
import {
	type GalleryItem,
	useServicePhotoUpload
} from '@/features/service-vetting/store/services-photo-upload.ts'
import { useUploadPhotos } from '@/hooks/use-upload-photos'
import { QUERY_KEYS } from '@/query-client'
import { type TKey, useTranslation } from '@/translations/i18n'
import type { FileToUpload } from '@/types/commons.ts'
import { useBulkPresignedServicePhotosProPhotos } from '../query/use-bulk-presigned-service-photos-pro-photos'
import type { ServiceProPhotos } from '../types'

export const ServiceVettingCriteriaGalleryUploadButtonProPhoto = ({
	serviceId,
	galleryId,
	disabled,
	buttonText,
	buttonProps,
	withIcon
}: {
	serviceId: number
	galleryId: number
	disabled?: boolean
	buttonText?: TKey
	buttonProps?: ButtonProps
	withIcon?: boolean
}) => {
	const { t } = useTranslation()
	const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
	const setUploadingItemsStatus = useServicePhotoUpload(
		(store) => store.setUploadingItemsStatus
	)
	const addUploadProgressValues = useServicePhotoUpload(
		(store) => store.addUploadProgressValues
	)
	const addGalleryItems = useServicePhotoUpload(
		(store) => store.addGalleryItems
	)
	const deleteGalleryItem = useServicePhotoUpload(
		(store) => store.deleteGalleryItem
	)
	const queryClient = useQueryClient()
	const uploadPhotos = useUploadPhotos({
		storeAction: { setUploadingItemsStatus, addUploadProgressValues },
		onConfirm: async (imageId) => {
			const response = await httpConfirmUploadPhotoProPhotos({
				urlParams: { serviceId, galleryId, mediaId: imageId },
				params: { responseType: 'extended' }
			})

			queryClient.invalidateQueries({
				queryKey: [
					QUERY_KEYS.SERVICE_GALLERY_IMAGES_PRO_PHOTOS,
					serviceId
				]
			})
			const queryKey = [QUERY_KEYS.SERVICE_PRO_PHOTOS, serviceId]
			await queryClient.cancelQueries({ queryKey })
			const previousData =
				queryClient.getQueryData<ServiceProPhotos>(queryKey)
			queryClient.setQueriesData<ServiceProPhotos>(
				{ queryKey },
				previousData
					? {
							...previousData,
							gallery: {
								...previousData.gallery,
								images: [
									...previousData.gallery.images,
									response
								].sort((a, b) => {
									if (a.position === null) {
										return 1
									}
									if (b.position === null) {
										return -1
									}
									return a.position - b.position
								})
							}
						}
					: previousData
			)

			deleteGalleryItem(imageId)
		}
	})

	const bulkPresignedQuery = useBulkPresignedServicePhotosProPhotos({
		urlParams: { serviceId, galleryId },
		options: {
			onSuccess: (data) => {
				const galleryItems: GalleryItem[] = data.map((item, index) => ({
					id: item.media.id,
					url: URL.createObjectURL(acceptedFiles[index]),
					status: 'pending',
					name: item.media.name,
					thumbnails: null //TODO: add thumbnails
				}))
				const uploadingItems: FileToUpload[] = data.map(
					(item, index) => ({
						id: item.media.id,
						presignedUrl: item.presignedUrl,
						file: acceptedFiles[index]
					})
				)
				addGalleryItems(galleryItems)
				uploadPhotos(uploadingItems)
			}
		}
	})

	const dropzoneState = useDropzone({
		disabled: bulkPresignedQuery.isPending,
		accept: {
			'image/jpeg': ['.jpeg'],
			'image/jpg': ['.jpg'],
			'image/png': ['.png']
		},
		maxFiles: VETTING_MAX_FILE_TO_UPLOAD,
		multiple: true,
		onDropAccepted: (acceptedFiles) => {
			setAcceptedFiles(acceptedFiles)
			bulkPresignedQuery.mutate({
				medias: acceptedFiles.map((file) => file.name),
				isOfferingImage: false
			})
		},
		onDropRejected: (rejectedFiles) => {
			if (rejectedFiles.length > VETTING_MAX_FILE_TO_UPLOAD) {
				return toast.error(
					t('errors:dropzone_max_file', {
						count: VETTING_MAX_FILE_TO_UPLOAD
					})
				)
			}
			console.error('Rejected files: ', rejectedFiles)
		}
	})

	return (
		<Dropzone state={dropzoneState}>
			<ButtonLegacy
				disabled={bulkPresignedQuery.isPending || disabled}
				size={'sm'}
				type='button'
				variant={'secondary'}
				{...buttonProps}
			>
				{bulkPresignedQuery.isPending ? (
					<Spinner size={17} />
				) : (
					withIcon && <PlusIcon />
				)}
				{t(
					buttonText ||
						'vetting:criteria.service.gallery.upload_photos'
				)}
			</ButtonLegacy>
		</Dropzone>
	)
}
